// src/components/Billing/BillManagementSection.js (Final Corrected Version)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAppContext } from '../../App';
import { api } from '../../apiService';
import { toast } from 'react-toastify';
import ConfirmationModal from '../Common/ConfirmationModal';

// --- Reusable Form for Templates and Editing Bills ---
function BillForm({ bill, template, onSave, onCancel, monthId, stationId, isTemplateMode = false }) {
    const [formData, setFormData] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const expenseCategories = ['Utilities', 'Rent'];

    useEffect(() => {
        const initialData = {
            billName: '', expenseCategory: 'Utilities', refNo: '', 
            dueDay: 15, defaultAmount: '', // For templates
            amount: '', dueDate: new Date().toISOString().split('T')[0] // For bills
        };
        const source = isTemplateMode ? template : bill;
        if (source) {
            setFormData({
                billName: source.billName || '',
                expenseCategory: source.expenseCategory || 'Utilities',
                refNo: source.refNo || '',
                // Template specific
                dueDay: source.dueDay || 15,
                defaultAmount: source.defaultAmount || '',
                // Bill specific
                amount: source.amount || '',
                dueDate: source.dueDate ? new Date(source.dueDate).toISOString().split('T')[0] : initialData.dueDate,
            });
        } else {
            setFormData(initialData);
        }
    }, [bill, template, isTemplateMode]);
    
    const handleChange = (e) => setFormData({ ...formData, [e.target.id]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            if (isTemplateMode) {
                // Saving a Template
                const templateData = {
                    billName: formData.billName,
                    expenseCategory: formData.expenseCategory,
                    refNo: formData.refNo,
                    dueDay: parseInt(formData.dueDay),
                    defaultAmount: formData.defaultAmount ? parseFloat(formData.defaultAmount) : 0,
                    stationId: stationId,
                };
                if (template) { // Editing existing template
                    await api.put('recurring_bill_templates', template.id, templateData);
                    toast.success("Template updated successfully!");
                } else { // Creating new template
                    await api.post('recurring_bill_templates', templateData);
                    toast.success("Template added successfully!");
                }
            } else {
                // Saving a Bill
                const billData = {
                    billName: formData.billName,
                    amount: parseFloat(formData.amount),
                    dueDate: formData.dueDate,
                    refNo: formData.refNo,
                    expenseCategory: formData.expenseCategory,
                    stationId: stationId,
                    monthId: monthId,
                };
                await api.put('bills', bill.id, billData);
                toast.success("Bill updated successfully!");
            }
            onSave();
        } catch (err) {
            toast.error(`Operation failed: ${err.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-lg">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">{isTemplateMode ? (template ? 'Edit Template' : 'Add New Template') : 'Edit Bill'}</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2"><label htmlFor="billName" className="block text-sm font-medium text-gray-700">Bill Name</label><input type="text" id="billName" value={formData.billName} onChange={handleChange} className="mt-1 w-full p-2 bg-gray-50 rounded-md border" required /></div>
                        <div><label htmlFor="expenseCategory" className="block text-sm font-medium text-gray-700">Category</label><select id="expenseCategory" value={formData.expenseCategory} onChange={handleChange} className="mt-1 w-full p-2 bg-gray-50 rounded-md border">{expenseCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}</select></div>
                        <div><label htmlFor="refNo" className="block text-sm font-medium text-gray-700">Reference #</label><input type="text" id="refNo" value={formData.refNo} onChange={handleChange} className="mt-1 w-full p-2 bg-gray-50 rounded-md border" /></div>
                        {isTemplateMode ? (<>
                            <div><label htmlFor="dueDay" className="block text-sm font-medium text-gray-700">Due Day (1-31)</label><input type="number" id="dueDay" value={formData.dueDay} onChange={handleChange} min="1" max="31" className="mt-1 w-full p-2 bg-gray-50 rounded-md border" required /></div>
                            <div><label htmlFor="defaultAmount" className="block text-sm font-medium text-gray-700">Default Amount (Optional)</label><input type="number" id="defaultAmount" value={formData.defaultAmount} onChange={handleChange} className="mt-1 w-full p-2 bg-gray-50 rounded-md border" /></div>
                        </>) : (<>
                            <div><label htmlFor="amount" className="block text-sm font-medium text-gray-700">Amount (PKR)</label><input type="number" id="amount" value={formData.amount} onChange={handleChange} className="mt-1 w-full p-2 bg-gray-50 rounded-md border" required /></div>
                            <div><label htmlFor="dueDate" className="block text-sm font-medium text-gray-700">Due Date</label><input type="date" id="dueDate" value={formData.dueDate} onChange={handleChange} className="mt-1 w-full p-2 bg-gray-50 rounded-md border" required /></div>
                        </>)}
                    </div>
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-800 font-bold py-2 px-6 rounded-lg">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="bg-rose-600 text-white font-bold py-2 px-6 rounded-lg disabled:bg-gray-400">{isSubmitting ? 'Saving...' : 'Save Changes'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// --- Main Component ---
function BillManagementSection() {
    const { user, currentStationId, allStations } = useAppContext();
    const [activeTab, setActiveTab] = useState('monthly');
    const [bills, setBills] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const autoGenKeyRef = useRef(null);
    
    const [deletingBill, setDeletingBill] = useState(null);
    const [deletingTemplate, setDeletingTemplate] = useState(null);
    
    const [editingBill, setEditingBill] = useState(null);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [showTemplateForm, setShowTemplateForm] = useState(false);

    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const monthId = `${filterYear}-${String(filterMonth).padStart(2, '0')}`;
    const isTemplateForStation = useCallback((t, stationId) => {
        const sid = String(stationId || '');
        const tSid = t?.stationId === null || t?.stationId === undefined ? '' : String(t.stationId);
        return tSid === sid;
    }, []);
    const isMixFm99Station = useCallback(() => {
        const st = (allStations || []).find(s => String(s.id) === String(currentStationId));
        if (!st) return false;
        const name = String(st.name || '');
        const freq = String(st.frequency || '');
        if (name.includes('99')) return true;
        const m = freq.match(/(\d+(\.\d+)?)/);
        if (!m) return false;
        const n = Number(m[1]);
        return Number.isFinite(n) && n >= 98.5 && n < 99.6;
    }, [allStations, currentStationId]);

    const handleAutomaticBillGeneration = useCallback(async () => {
        if (!currentStationId) return;
        setIsProcessing(true);
        toast.info("Checking for monthly bills...", { autoClose: 1500, toastId: 'bill-check' });
        
        try {
            const existingBills = await api.get('bills', { where: { stationId: currentStationId, monthId: monthId }, limit: 1 });

            if (existingBills.length > 0) {
                setIsProcessing(false);
                return;
            }

            const templatesToGenerate = await api.get('recurring_bill_templates', { where: { stationId: currentStationId } });
            
            if (templatesToGenerate.length === 0) {
                setIsProcessing(false);
                return;
            }
            
            const newBills = templatesToGenerate.map(template => ({
                billName: template.billName, refNo: template.refNo || '', amount: template.defaultAmount || 0,
                dueDate: new Date(filterYear, filterMonth - 1, template.dueDay).toISOString().slice(0, 10),
                expenseCategory: template.expenseCategory, stationId: currentStationId, monthId: monthId,
                status: 'Pending', templateId: template.id,
            }));

            // This assumes your API can handle a batch creation
            await api.post('bills/batch', newBills);
            toast.success(`${newBills.length} bills automatically generated for this month!`);

        } catch (err) {
            toast.error(`Auto-generation failed: ${err.message}`);
        } finally {
            setIsProcessing(false);
        }
    }, [currentStationId, filterMonth, filterYear, monthId]);


    // Fetch monthly bills and trigger auto-generation
    useEffect(() => {
        if (activeTab === 'monthly' && currentStationId) {
            const key = `${currentStationId}|${monthId}`;
            if (autoGenKeyRef.current !== key) {
                autoGenKeyRef.current = key;
                handleAutomaticBillGeneration();
            }
        }

        if (activeTab !== 'monthly' || !currentStationId) {
            setBills([]); setIsLoading(false); return;
        }
        setIsLoading(true);
        const fetchBills = async () => {
            try {
                const data = await api.get('bills', { where: { stationId: currentStationId, monthId: monthId }, orderBy: 'dueDate ASC' });
                const unique = new Map();
                (data || []).forEach((b) => {
                    const templateKey = b.templateId !== undefined && b.templateId !== null && String(b.templateId).trim() !== '' ? `t:${b.templateId}` : '';
                    const fallbackKey = `n:${b.billName || ''}|${b.refNo || ''}|${b.dueDate || ''}|${b.amount || ''}`;
                    const key = templateKey || fallbackKey;
                    if (!unique.has(key)) unique.set(key, b);
                });
                setBills(Array.from(unique.values()));
            } catch (err) {
                toast.error(`Failed to fetch bills: ${err.message}`);
            } finally {
                setIsLoading(false);
            }
        };
        fetchBills();
    }, [currentStationId, monthId, activeTab, handleAutomaticBillGeneration]);

    // Fetch templates
    useEffect(() => {
        if (activeTab !== 'templates') { setIsLoading(false); return; }
        if (!currentStationId) { setTemplates([]); setIsLoading(false); return; }
        setIsLoading(true);
        const fetchTemplates = async () => {
            try {
                const data = await api.get('recurring_bill_templates', { orderBy: 'billName' });
                const all = data || [];
                const unassigned = all.filter(t => !t.stationId);
                if (unassigned.length > 0 && isMixFm99Station()) {
                    await Promise.all(unassigned.map(t => api.put('recurring_bill_templates', t.id, { stationId: currentStationId })));
                    const refreshed = await api.get('recurring_bill_templates', { where: { stationId: currentStationId }, orderBy: 'billName' });
                    const byId = new Map((refreshed || []).map(t => [String(t.id), t]));
                    setTemplates(Array.from(byId.values()));
                    return;
                }
                const filtered = all.filter(t => isTemplateForStation(t, currentStationId));
                const byId = new Map(filtered.map(t => [String(t.id), t]));
                setTemplates(Array.from(byId.values()));
            } catch (err) {
                toast.error(`Failed to fetch templates: ${err.message}`);
            } finally {
                setIsLoading(false);
            }
        };
        fetchTemplates();
    }, [activeTab, currentStationId, isTemplateForStation, isMixFm99Station]);
    
    const handlePayBill = async (bill) => {
        if (bill.status === 'Paid' || bill.amount <= 0) {
            if(bill.amount <= 0) toast.warn("Cannot pay a bill with zero amount. Please edit the bill first.");
            return;
        }
        setIsProcessing(true);
        try {
            await api.put('bills', bill.id, { status: 'Paid', paymentDate: new Date().toISOString() });
            await api.post('expenses', {
                description: `Paid: ${bill.billName} (Ref: ${bill.refNo || 'N/A'})`, amount: bill.amount, category: bill.expenseCategory,
                stationId: bill.stationId, date: new Date().toISOString(),
            });
            toast.success("Bill paid and expense recorded!");
        } catch (err) { toast.error(`Payment failed: ${err.message}`); } finally { setIsProcessing(false); }
    };
    
    const handleDeleteBill = async () => { if (!deletingBill) return; try { await api.delete('bills', deletingBill.id); toast.success("Bill deleted."); } catch (err) { toast.error(`Failed to delete bill: ${err.message}`); } finally { setDeletingBill(null); }};
    const handleDeleteTemplate = async () => { if (!deletingTemplate) return; try { await api.delete('recurring_bill_templates', deletingTemplate.id); toast.success("Template deleted."); } catch (err) { toast.error(`Failed to delete template: ${err.message}`); } finally { setDeletingTemplate(null); }};

    return (
        <div className="container mx-auto">
            {deletingBill && <ConfirmationModal isOpen={true} onClose={() => setDeletingBill(null)} onConfirm={handleDeleteBill} title="Delete Bill">Are you sure you want to delete bill: <strong>{deletingBill?.billName}</strong>?</ConfirmationModal>}
            {deletingTemplate && <ConfirmationModal isOpen={true} onClose={() => setDeletingTemplate(null)} onConfirm={handleDeleteTemplate} title="Delete Template">Are you sure you want to delete template: <strong>{deletingTemplate?.billName}</strong>?</ConfirmationModal>}
            {editingBill && <BillForm bill={editingBill} onSave={() => setEditingBill(null)} onCancel={() => setEditingBill(null)} monthId={monthId} stationId={currentStationId} />}
            {showTemplateForm && <BillForm isTemplateMode={true} template={editingTemplate} onSave={() => { setShowTemplateForm(false); setEditingTemplate(null); }} onCancel={() => { setShowTemplateForm(false); setEditingTemplate(null); }} stationId={currentStationId} />}

            <div className="mb-8"><h1 className="text-4xl font-bold text-gray-800">Bill Management</h1><p className="text-gray-500 mt-1">Track and manage all your recurring monthly bills.</p></div>

            <div className="bg-white p-6 rounded-2xl shadow-lg mb-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                     <div className="flex border-b md:border-b-0"><button onClick={() => setActiveTab('monthly')} className={`px-4 py-2 text-lg font-semibold ${activeTab === 'monthly' ? 'text-rose-600 border-b-2 border-rose-600' : 'text-gray-500'}`}>Monthly Bills</button><button onClick={() => setActiveTab('templates')} className={`px-4 py-2 text-lg font-semibold ${activeTab === 'templates' ? 'text-rose-600 border-b-2 border-rose-600' : 'text-gray-500'}`}>Manage Templates</button></div>
                     {activeTab === 'monthly' && (<div className="flex items-center gap-4"><select value={filterMonth} onChange={e => setFilterMonth(parseInt(e.target.value))} className="p-2 bg-gray-50 rounded-md border">{Array.from({ length: 12 }, (_, i) => <option key={i+1} value={i+1}>{new Date(0, i).toLocaleString('en-US', { month: 'long' })}</option>)}</select><select value={filterYear} onChange={e => setFilterYear(parseInt(e.target.value))} className="p-2 bg-gray-50 rounded-md border">{Array.from({ length: 5 }, (_, i) => <option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>)}</select></div>)}
                </div>

                {activeTab === 'templates' && (<div className="mt-6"><div className="text-right mb-4"><button onClick={() => { setEditingTemplate(null); setShowTemplateForm(true); }} className="bg-rose-600 text-white font-bold py-2 px-4 rounded-lg shadow-md hover:bg-rose-700">+ Add New Template</button></div><div className="overflow-x-auto"><table className="w-full text-sm text-left text-gray-500 mt-4"><thead className="text-xs text-gray-700 uppercase bg-gray-50"><tr><th className="px-6 py-3">Bill Name</th><th className="px-6 py-3">Category</th><th className="px-6 py-3 text-center">Due Day</th><th className="px-6 py-3 text-right">Actions</th></tr></thead><tbody>{isLoading ? (<tr><td colSpan="4" className="p-8 text-center">Loading...</td></tr>) : templates.map(t => (<tr key={t.id} className="bg-white border-b hover:bg-gray-50"><td className="px-6 py-4 font-semibold">{t.billName}</td><td className="px-6 py-4">{t.expenseCategory}</td><td className="px-6 py-4 text-center">{t.dueDay}</td><td className="px-6 py-4 text-right whitespace-nowrap"><button onClick={() => { setEditingTemplate(t); setShowTemplateForm(true); }} className="font-semibold text-blue-600">Edit</button><span className="mx-2 text-gray-300">|</span><button onClick={() => setDeletingTemplate(t)} className="font-semibold text-red-600">Delete</button></td></tr>))}</tbody></table></div></div>)}
                
                {activeTab === 'monthly' && (<div className="mt-6"><div className="overflow-x-auto"><table className="w-full text-sm text-left text-gray-500"><thead className="text-xs text-gray-700 uppercase bg-gray-50"><tr><th className="px-6 py-3">Bill Details</th><th className="px-6 py-3">Due Date</th><th className="px-6 py-3 text-right">Amount</th><th className="px-6 py-3 text-center">Status</th><th className="px-6 py-3 text-center">Actions</th></tr></thead><tbody>{isLoading ? (<tr><td colSpan="5" className="p-8 text-center">Loading...</td></tr>) : bills.length === 0 ? (<tr><td colSpan="5" className="p-8 text-center text-gray-500">{isProcessing ? "Checking/Generating bills..." : "No bills found. Check the 'Manage Templates' tab."}</td></tr>) : bills.map(bill => { const isPaid = bill.status === 'Paid'; const isOverdue = !isPaid && new Date(bill.dueDate) < new Date(); return(<tr key={bill.id} className="bg-white border-b hover:bg-gray-50"><td className="px-6 py-4"><div className="font-semibold text-gray-800">{bill.billName}</div><div className="text-xs text-gray-500">Ref: {bill.refNo || 'N/A'}</div></td><td className="px-6 py-4">{new Date(bill.dueDate).toLocaleDateString()}</td><td className="px-6 py-4 text-right font-bold">PKR {bill.amount.toLocaleString()}</td><td className="px-6 py-4 text-center"><span className={`px-2 py-1 text-xs font-semibold rounded-full ${isPaid ? 'bg-green-100 text-green-800' : isOverdue ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>{isPaid ? 'Paid' : isOverdue ? 'Overdue' : 'Pending'}</span></td><td className="px-6 py-4 text-center whitespace-nowrap"><button onClick={() => handlePayBill(bill)} disabled={isPaid || isProcessing} className="font-semibold text-green-600 disabled:text-gray-400">Pay</button><span className="mx-2 text-gray-300">|</span><button onClick={() => setEditingBill(bill)} disabled={isPaid} className="font-semibold text-blue-600 disabled:text-gray-400">Edit</button>{user.role === 'admin' && <><span className="mx-2 text-gray-300">|</span><button onClick={() => setDeletingBill(bill)} className="font-semibold text-red-600">Delete</button></>}</td></tr>)})}</tbody></table></div></div>)}
            </div>
        </div>
    );
}

export default BillManagementSection;
